import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useCollectionBinding,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';
import { PropertyContactInfo } from '@vibesites/theme-preferred/components/PropertyContactInfo';
import type { OfficeHourEntry } from '@vibesites/theme-preferred/components/PropertyContactInfo';

const PREFIX = 'scheduleatour';

/** Tokens are stored as strings; interpret a toggle token as a boolean. */
function isTruthy(value: string | null): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === '1';
}

export function ScheduleATour() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const showTourScheduler = isTruthy(useContentToken(`${PREFIX}.show_tour_scheduler`));
  const tourSlot = useBlockSlot(`${PREFIX}.tour_block`, { defaultValue: 'multihub-tour-scheduler' });

  const visitLabel = useContentToken(`${PREFIX}.visit_label`);
  const callLabel  = useContentToken(`${PREFIX}.call_label`);
  const hoursLabel = useContentToken(`${PREFIX}.hours_label`);
  const address    = useContentToken(`${PREFIX}.address`);
  const phoneToken = useContentToken(`${PREFIX}.phone`);
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  const phone = phoneToken || fallbackPhone || '';

  const officeHours = useCollectionBinding<OfficeHourEntry>(`${PREFIX}.office_hours`, {
    collection: 'office_hours',
  });

  return (
    <section ref={sectionRef} className="reveal-fade-up sat-section">
      <div className={`sat-container${showTourScheduler ? ' sat-container--wide' : ''}`}>
        <div className={`sat-grid${showTourScheduler ? ' sat-grid--two' : ''}`}>
          <div className="sat-info-col">
            <PropertyContactInfo
              prefix={PREFIX}
              visitLabel={visitLabel}
              callLabel={callLabel}
              hoursLabel={hoursLabel}
              address={address}
              phone={phone}
              officeHours={officeHours}
            />
          </div>

          {showTourScheduler && (
            <div className="sat-form-col">
              <EditableToken path="scheduleatour.tour_block" as="div">
                {tourSlot ? (
                  <SectionContextProvider sectionId={tourSlot.sectionId}>
                    <tourSlot.BlockComponent sectionId={tourSlot.sectionId} />
                  </SectionContextProvider>
                ) : null}
              </EditableToken>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .sat-section {
          padding: 2.5rem 0 3rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .sat-section { padding: 3rem 0 3.5rem; }
        }
        .sat-container {
          max-width: 40rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        .sat-container--wide { max-width: 72rem; }
        @media (min-width: 640px) { .sat-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sat-container { padding: 0 2rem; } }

        .sat-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .sat-grid--two {
            grid-template-columns: 5fr 7fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        .sat-form-col { position: relative; }
      `}</style>

      {/* Schema-lint anchors — PropertyContactInfo uses dynamic `${prefix}.*` paths
          on its EditableTokens, so these hidden elements satisfy token-missing-editable-wrapper. */}
      <EditableToken path="scheduleatour.visit_label"         as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.address"             as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.call_label"          as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.phone"               as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.hours_label"         as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduleatour.show_tour_scheduler" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleATour',
  displayName: 'Schedule A Tour',
  tokens: {
    'scheduleatour.tour_block': {
      label: 'Tour Scheduler Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduleatour.show_tour_scheduler': {
      label: 'Show Tour Scheduler',
      type: 'text',
      defaultValue: 'false',
    },
    'scheduleatour.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduleatour.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'scheduleatour.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduleatour.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    'scheduleatour.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  collectionBindings: {
    'scheduleatour.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleATour as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleATour;
